import { area } from '@visx/shape';
import {
    AddSVGProps,
    BaseAreaProps,
    PositionScale,
} from '@visx/shape/lib/types';
import cx from 'classnames';
import { motion, MotionProps } from 'framer-motion';
import React from 'react';
import { setNumberOrNumberAccessor as setNumOrAccessor } from './helpers';

type BaseProps<Datum> = BaseAreaProps<Datum> & {
    yScale: PositionScale;
};

export type AreaClosedProps<Datum> = Omit<
    AddSVGProps<BaseProps<Datum>, SVGPathElement>,
    'ref'
> &
    MotionProps;

/**
 * Port of visx's AreaClosed component
 *
 * ref: https://github.com/airbnb/visx/blob/master/packages/visx-shape/src/shapes/AreaClosed.tsx
 */
export default function AreaClosed<Datum>({
    x,
    x0,
    x1,
    y,
    y1,
    y0,
    yScale,
    data = [],
    defined = () => true,
    className,
    curve,
    innerRef,
    children,
    ...restProps
}: AreaClosedProps<Datum>) {
    const path = area<Datum>({ x, x0, x1, defined, curve });
    if (y0 === undefined) {
        /**
         * by default set the baseline to the minimum of the yRange
         * this diverges from @visx's internal logic
         */
        path.y0(yScale.domain()[0]);
    } else {
        setNumOrAccessor(path.y0, y0);
    }
    if (y && !y1) setNumOrAccessor(path.y1, y);
    if (y1 && !y) setNumOrAccessor(path.y1, y1);
    if (children) return <>{children({ path })}</>;
    return (
        <motion.path
            ref={innerRef}
            className={cx('visx-area-closed', className)}
            animate={{
                d: path(data) ?? '',
            }}
            {...restProps}
        />
    );
}
